import type { HomecadDocument } from "@homecad/schema";
import type {
  EvaluationResult,
  ReglagesDebit,
  SourceChargee,
  WorkerRequest,
  WorkerRequestBody,
  WorkerResponse,
} from "./protocol.js";

/**
 * Face avant du worker, cote interface. Transforme le va-et-vient de messages
 * en promesses, et garde trace de la derniere requete de chaque nature : quand
 * l'utilisateur tire un slider il en part une par image, et afficher une reponse
 * arrivee en retard ferait clignoter le viewport sur un etat perime.
 */
export class KernelClient {
  readonly #worker: Worker;
  readonly #pending = new Map<number, { resolve: (r: WorkerResponse) => void; reject: (e: Error) => void }>();
  readonly #latest = new Map<WorkerRequest["kind"], number>();
  #nextId = 1;

  constructor() {
    this.#worker = new Worker(new URL("./worker.ts", import.meta.url), { type: "module" });
    this.#worker.addEventListener("message", (event: MessageEvent<WorkerResponse>) => {
      const response = event.data;
      const entry = this.#pending.get(response.id);
      if (entry === undefined) return;
      this.#pending.delete(response.id);
      entry.resolve(response);
    });
    this.#worker.addEventListener("error", (event) => {
      const error = new Error(`Worker geometrique : ${event.message}`);
      for (const entry of this.#pending.values()) entry.reject(error);
      this.#pending.clear();
    });
  }

  /** Vrai si cette reponse est la plus recente de sa nature. */
  isCurrent(kind: WorkerRequest["kind"], id: number): boolean {
    return this.#latest.get(kind) === id;
  }

  async init(): Promise<void> {
    await this.#send({ kind: "init" });
  }

  async evaluate(document: HomecadDocument): Promise<{ id: number; result: EvaluationResult }> {
    const { id, response } = await this.#sendTracked({ kind: "evaluate", document });
    if (!response.ok) throw new Error(response.error);
    if (response.kind !== "evaluate") throw new Error("Reponse inattendue du worker");
    return { id, result: response.result };
  }

  /** Sans `bodyId`, toute la piece en un fichier ; avec, ce seul corps. */
  async exportStl(document: HomecadDocument, bodyId?: string): Promise<ArrayBuffer> {
    // `exactOptionalPropertyTypes` distingue « absent » de « vaut undefined » :
    // la requete se construit sans la clef plutot qu'avec une clef vide.
    const response = await this.#send(
      bodyId === undefined
        ? { kind: "exportStl", document }
        : { kind: "exportStl", document, bodyId },
    );
    if (!response.ok) throw new Error(response.error);
    if (response.kind !== "exportStl") throw new Error("Reponse inattendue du worker");
    return response.data;
  }

  /**
   * Confie un fichier dessine ailleurs au worker. Le solide y RESTE : ce qui
   * revient n'est qu'un etat des lieux — nom, volume, encombrement.
   */
  async charger(nom: string, octets: ArrayBuffer): Promise<SourceChargee> {
    const response = await this.#send({ kind: "charger", nom, octets });
    if (!response.ok) throw new Error(response.error);
    if (response.kind !== "charger") throw new Error("Reponse inattendue du worker");
    return response.source;
  }

  /** Debite la source chargee. Rejouable sans relire le fichier. */
  async debiter(reglages: ReglagesDebit): Promise<{ id: number; result: EvaluationResult }> {
    const { id, response } = await this.#sendTracked({ kind: "debiter", reglages });
    if (!response.ok) throw new Error(response.error);
    if (response.kind !== "debiter") throw new Error("Reponse inattendue du worker");
    return { id, result: response.result };
  }

  async exporterMorceau(bodyId: string): Promise<ArrayBuffer> {
    const response = await this.#send({ kind: "exporterMorceau", bodyId });
    if (!response.ok) throw new Error(response.error);
    if (response.kind !== "exporterMorceau") throw new Error("Reponse inattendue du worker");
    return response.data;
  }

  terminate(): void {
    this.#worker.terminate();
  }

  async #send(request: WorkerRequestBody): Promise<WorkerResponse> {
    return (await this.#sendTracked(request)).response;
  }

  async #sendTracked(
    request: WorkerRequestBody,
  ): Promise<{ id: number; response: WorkerResponse }> {
    const id = this.#nextId++;
    const full = { ...request, id } as WorkerRequest;
    this.#latest.set(full.kind, id);
    const response = await new Promise<WorkerResponse>((resolve, reject) => {
      this.#pending.set(id, { resolve, reject });
      this.#worker.postMessage(full);
    });
    return { id, response };
  }
}
